/**
 * mapTalentToProfileReview
 *
 * Pure mapper from a Talent-like input (the citizen's profile data
 * stored across `talents.customFields` JSONB plus a few core columns)
 * into the canonical `IOnboardingData` shape that ProfileReview
 * renders.
 *
 * Defined here (in `@tonnie/profile-review`) rather than in
 * `@tonnie/sdk` because the SDK is a transport layer and explicitly
 * forbids data transformations (see `packages/sdk/CLAUDE.md`).
 *
 * The input type is structural — it accepts ANY object with the
 * relevant fields. Both consumers pass:
 *   • journey-web — `useUserProfile().data.talent` (with avatar from
 *     auth on top)
 *   • business-web — `useTalent(id).data` (no avatar override)
 *
 * The function never throws and gracefully degrades when fields are
 * missing — useful for partial profiles (citizens mid-onboarding).
 */

import type { IOnboardingData } from './types';

/**
 * Minimal structural shape we need. Both `useUserProfile().data.talent`
 * and `useTalent(id).data` satisfy this — they share the same wider
 * Talent interface in the SDK, and we only depend on this subset.
 */
export interface ProfileReviewInputTalent {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
  /**
   * Every citizenship, canonical first, as the server resolved it
   * (`GET /users/me` → `talent.citizenships`; the business talent detail).
   */
  citizenships?: string[] | null;
  /** Free-form skills jsonb (talents.skills). Used as a fallback when
   *  `customFields.skills` is missing. */
  skills?: Record<string, unknown> | null;
  /** The big onboarding-data bag (talents.custom_fields). */
  customFields?: Record<string, unknown> | null;
}

export interface MapTalentToProfileReviewOptions {
  /**
   * Avatar URL override. Useful when the consumer has a more
   * authoritative source than `customFields.avatarUrl` — e.g.
   * journey-web pulls it from the Clerk auth user. Pass `undefined`
   * to fall back to whatever the citizen uploaded during onboarding.
   */
  avatarUrl?: string | null;
}

export function mapTalentToProfileReview(
  talent: ProfileReviewInputTalent | null | undefined,
  options: MapTalentToProfileReviewOptions = {}
): IOnboardingData {
  if (!talent) return {};

  const cf = (talent.customFields ?? {}) as Record<string, unknown>;

  return {
    avatarUrl: options.avatarUrl ?? (cf['avatarUrl'] as string | undefined) ?? undefined,
    firstName: talent.firstName ?? undefined,
    lastName: talent.lastName ?? undefined,
    dateOfBirth: (cf['dateOfBirth'] as string | undefined) ?? talent.dateOfBirth ?? undefined,
    phone: talent.phone ?? undefined,
    phoneCountryCode: cf['phoneCountryCode'] as string | undefined,
    address: cf['address'] as IOnboardingData['address'],
    websiteUrls: cf['websiteUrls'] as IOnboardingData['websiteUrls'],
    workModelPreference: cf['workModelPreference'] as string[] | undefined,
    citizenships: talent.citizenships ?? [],
    mobility: cf['mobility'] as IOnboardingData['mobility'],
    familyConfig: cf['familyConfig'] as IOnboardingData['familyConfig'],
    skills:
      (cf['skills'] as IOnboardingData['skills']) ?? (talent.skills as IOnboardingData['skills']),
    education: cf['education'] as IOnboardingData['education'],
    experience: cf['experience'] as IOnboardingData['experience'],
  };
}
